// Nine Lives '84 - the birdcage room.
//
// A cage on a table in the middle of the room. Get up on the table and
// walk into the cage to nudge it toward the edge; every nudge throws
// Tabs back to the floor. When it goes over it breaks, and the bird is
// loose: catch it in the air. A dog patrols the floor, and its bite costs
// a life (though not, as in the original, while the broom has you).

import { knockCat, type Cat } from '../cat';
import {
  BIRD_H,
  BIRD_W,
  CAGE_H,
  CAGE_W,
  DOG_H,
  DOG_W,
  DT,
  GRAVITY,
  NUDGE_SLIDE,
  WORLD_H,
  WORLD_W,
} from '../constants';
import { overlaps } from '../physics';
import { chaseDog, Room, type RoomContext, type RoomDog } from '../room';
import type { Box, GameEvent, Platform, RoomKind } from '../types';

export interface Cage extends Box {
  state: 'up' | 'falling' | 'broken';
  vx: number;
  vy: number;
}

export interface Bird extends Box {
  vx: number;
  vy: number;
  /** Phase of the flapping bob. */
  t: number;
}

export class BirdcageRoom extends Room {
  readonly kind: RoomKind = 'birdcage';
  /** The table top, a standing hop from the floor; Tabs and the dog pass beneath it. */
  readonly table: Platform = { kind: 'furniture', x: 118, y: 31, w: 84, h: 3, oneWay: true };
  readonly cage: Cage;
  readonly dog: RoomDog;
  bird: Bird | null = null;
  nudges = 0;
  birdCaught = false;

  constructor(ctx: RoomContext) {
    super(ctx);
    this.platforms.push(this.table);
    this.cage = {
      x: this.table.x + this.table.w / 2 - CAGE_W / 2,
      y: this.table.y + this.table.h,
      w: CAGE_W,
      h: CAGE_H,
      state: 'up',
      vx: 0,
      vy: 0,
    };
    this.dog = { x: 250, y: 0, w: DOG_W, h: DOG_H, dir: -1 };
  }

  get taskName(): string {
    return this.cage.state === 'up' ? 'nudges' : 'bird';
  }

  get need(): number {
    return this.cage.state === 'up' ? this.ctx.params.cageNudges : 1;
  }

  get have(): number {
    return this.cage.state === 'up' ? this.nudges : this.birdCaught ? 1 : 0;
  }

  protected update(cat: Cat, _keys: number, events: GameEvent[]): void {
    this.updateCage(cat, events);
    this.updateBird(cat, events);
    if (
      chaseDog(
        this.dog,
        cat,
        this.ctx.params.dogSpeed,
        this.ctx.params.dogCharge,
        30,
        WORLD_W,
      )
    ) {
      this.fatal = 'dog';
    }
  }

  private updateCage(cat: Cat, events: GameEvent[]): void {
    const c = this.cage;
    const t = this.table;
    if (c.state === 'up') {
      if (cat.ground === t && cat.knockedTicks === 0 && overlaps(cat, c)) {
        this.nudges++;
        c.x += cat.facing * NUDGE_SLIDE;
        events.push({ kind: 'nudge', have: this.nudges, need: this.need });
        knockCat(cat, -cat.facing * 120, 70, 'cage', events);
        const cx = c.x + c.w / 2;
        if (this.nudges >= this.ctx.params.cageNudges || cx < t.x || cx > t.x + t.w) {
          c.state = 'falling';
          c.vx = cat.facing * 30;
          c.vy = 10;
          // Make sure it clears the edge it is going over.
          c.x = cat.facing > 0 ? Math.max(c.x, t.x + t.w - c.w / 2) : Math.min(c.x, t.x - c.w / 2);
        }
      }
    } else if (c.state === 'falling') {
      c.vy -= GRAVITY * DT;
      c.x += c.vx * DT;
      c.y += c.vy * DT;
      if (c.y <= 0) {
        c.y = 0;
        c.state = 'broken';
        this.bird = {
          x: c.x + c.w / 2 - BIRD_W / 2,
          y: c.h,
          w: BIRD_W,
          h: BIRD_H,
          vx: cat.facing * this.ctx.params.birdSpeed,
          vy: 40,
          t: 0,
        };
        events.push({ kind: 'cageBroke' });
      }
    }
  }

  private updateBird(cat: Cat, events: GameEvent[]): void {
    const b = this.bird;
    if (!b || this.birdCaught) return;
    const speed = this.ctx.params.birdSpeed;
    b.t += DT;
    // Flap along a wavy path, turning at the walls and now and then at whim.
    b.vy = Math.sin(b.t * 3.2) * 45;
    b.x += b.vx * DT;
    b.y += b.vy * DT;
    if (b.x < 0) {
      b.x = 0;
      b.vx = speed;
    } else if (b.x + b.w > WORLD_W) {
      b.x = WORLD_W - b.w;
      b.vx = -speed;
    }
    if (this.ctx.rng.chance(0.006)) b.vx = -b.vx;
    b.y = Math.max(24, Math.min(WORLD_H - 30, b.y));
    if (cat.knockedTicks === 0 && overlaps(cat, b)) {
      this.birdCaught = true;
      this.done = true;
      events.push({ kind: 'birdCaught' });
    }
  }
}
